import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Card,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogContent,
  Divider,
  alpha,
  CardMedia,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  KeyboardBackspace,
  ContentCopy,
  PhotoLibrary as PhotoLibraryIcon,
  HomeWork as HomeWorkIcon,
  CalendarMonth as CalendarMonthIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  Call as CallIcon,
  School as SchoolIcon,
  Roofing as RoofingIcon,
  AcUnit as AcUnitIcon,
  WaterDrop as WaterDropIcon,
  ElectricalServices as ElectricalServicesIcon,
  MeetingRoom as MeetingRoomIcon,
  LocalParking as LocalParkingIcon,
  Fireplace as FireplaceIcon,
  AttachMoney as AttachMoneyIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  Numbers as NumbersIcon,
} from '@mui/icons-material';
import {
  IsNotNullOrEmpty,
  IsNullOrEmpty,
  isAuthTokenExpired,
} from '../../utils/validation';
import WidgetAPI from '../../services/api';
import { WidgetConfig } from '../../types';
import { getAnonymousToken, getAuthToken } from '../../utils/storage';
import useToggle from '../../hooks/useToggle';
import KeyFeatureDailog from '../KeyFeatureDailog';
import { getAssetURL } from '../../utils/url';
import { InteractionType, LeadActionType } from '../../enum';
import { toast } from 'react-toastify';
import PreOnboardingDetails from './PreOnboardingDetails';
import PaymentInformationModel from './PaymentInformationModel';
import OnboardingForm from './OnboardingForm';
import OTPVerification from './OTPVerification';
import MilesTimeSlotPicker from '../MilesTimeSlotPicker';
import ViewSimilarPropertiesDialog from './ViewSimilarPropertiesDialog';
import { getUserTimezone } from '../../utils/timezone';
import Loader from '../Loader';
import AgentContactDialog from './AgentContactDialog';

export const BACKGROUND_GRADIENT =
  'linear-gradient(to bottom, #ffffff, #B1CAFF)';
export const BACKGROUND_GRADIENT_TRANSPARENT =
  'linear-gradient(to bottom, rgba(255, 255, 255, 0.5), rgba(177, 202, 255, 0.5))';
export const BUTTON_BG_COLOR = '#2a80f5';
export const BUTTON_BG_COLOR_GREEN = '#00BE62';


const INTERACTION_TYPE_BY_QUICK_OPTION: Partial<
  Record<NewLeadQuickOption, InteractionType>
> = {
  schedule_test_drive: InteractionType.scheduleShowing,
  request_finance: InteractionType.requestFinance,
  price_and_payments: InteractionType.paymentCalculator,
  history: InteractionType.priceHistory,
  share_property: InteractionType.shareProperty,
  value_trade: InteractionType.marketAnalysis,
  virtual_walkaround: InteractionType.virtualTour,
  similar_properties: InteractionType.compareProperties,
};

// Types
type NewLeadDialogStep = 'information' | 'onboarding' | 'otp' | 'confirmation';

export type NewLeadQuickOption =
  | 'schedule_test_drive'
  | 'request_finance'
  | 'price_and_payments'
  | 'history'
  | 'save_vehicle'
  | 'share_property'
  | 'live_agent'
  | 'value_trade'
  | 'virtual_walkaround'
  | 'similar_properties'
  | 'contact_dealer';

type Nullable<T> = T | null;

interface PropertyLocation {
  crs?: {
    type?: string;
    properties?: {
      name?: string;
    };
  };
  type?: string;
  coordinates?: [number, number];
}

interface PropertyPhoto {
  id: string;
  propertyId?: string;
  fileName?: string;
  fileType?: string;
  key?: string;
  url?: string;
  title?: string;
  position?: number;
}

interface PropertyAssociation {
  id: string;
  propertyId: string;
  associationYn?: boolean;
  associationFee?: Nullable<number>;
  associationFeeFrequency?: Nullable<string>;
  associationFrequency?: Nullable<string>;
  associationAmenities?: Nullable<string>;
  associationName?: Nullable<string>;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface PropertyFeatures {
  id: string;
  propertyId: string;
  newConstructionYn?: boolean;
  basement?: string[];
  basementYn?: boolean;
  attachedGarageYn?: boolean;
  garageSpaces?: Nullable<number>;
  garageYn?: boolean;
  carportSpaces?: Nullable<number>;
  carportYn?: boolean;
  parkingTotal?: Nullable<number>;
  parkingFeatures?: string[];
  architecturalStyle?: string[];
  construction?: Nullable<string>;
  constructionMaterials?: string[];
  roof?: string[];
  foundation?: Nullable<string>;
  exteriorFeatures?: string[];
  fencing?: string[];
  interiorFeatures?: string[];
  appliances?: string[];
  flooring?: string[];
  fireplacesTotal?: Nullable<number>;
  fireplaceTotal?: Nullable<number>;
  fireplaceFeatures?: string[] | string | null;
  fireplaceYn?: boolean;
  heating?: string[];
  heatingFuel?: Nullable<string>;
  cooling?: string[];
  coolingYn?: boolean;
  electric?: string[];
  gas?: Nullable<string>;
  sewer?: Nullable<string>;
  waterSource?: Nullable<string>;
  utilities?: string[];
  pool?: Nullable<string>;
  poolYn?: boolean;
  poolFeatures?: string[];
  spaYn?: boolean;
  spaFeatures?: string[];
  patioAndPorchFeatures?: string[];
  viewYn?: boolean;
  view?: string[];
  accessibilityFeatures?: string[];
  communityFeatures?: string[];
  seniorCommunityYn?: boolean;
  roadFrontageType?: string[];
  roadSurfaceType?: string[];
  waterfrontYn?: boolean;
  waterfrontFeatures?: string[] | string | null;
  horseYn?: boolean;
  horseAmenities?: string[] | string | null;
  rentIncludes?: string[] | string | null;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface PropertyRemarks {
  id: string;
  propertyId: string;
  publicRemarks?: Nullable<string>;
  privateRemarks?: Nullable<string>;
  showingInstructions?: Nullable<string>;
  directionFaces?: Nullable<string>;
  listingTerms?: Nullable<string>;
  disclosures?: Nullable<string>;
  specialListingConditions?: Nullable<string>;
  possessionTerms?: Nullable<string>;
  remarks?: Nullable<string>;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface PropertyCategory {
  id: string;
  name: string;
  position?: number;
  activeState?: boolean;
  propertyCategoryId?: string;
}

interface PropertyMlsPlatform {
  id: string;
  code?: string;
  title?: string;
  description?: string;
  keyCsvColumnNames?: string;
  position?: number;
  activeState?: boolean;
}

interface PropertyOpenHouse {
  date?: Nullable<string>;
  startDate?: Nullable<string>;
  time?: Nullable<string>;
  startTime?: Nullable<string>;
}

interface LegacyAgentSettings {
  dealershipWebsite?: string;
  dealershipEmail?: string;
  dealershipPhone?: string;
  dealershipAddress?: string;
  dealershipHours?: string;
  dealershipSpecialOffers?: string;
  financeAppLink?: string;
}

interface LegacyAgent {
  id?: string;
  firstName?: string;
  lastName?: string;
  dealerNames?: string[];
  dealerSettings?: LegacyAgentSettings;
  agentSettings?: LegacyAgentSettings;
}

interface PropertyResponse {
  id: string;
  mlsId?: string;
  listingId?: string;
  listPrice?: Nullable<number>;
  price?: Nullable<number | string>;
  listDate?: Nullable<string>;
  type?: Nullable<string>;
  subType?: Nullable<string>;
  categoryId?: Nullable<string>;
  subCategoryId?: Nullable<string>;
  link?: Nullable<string>;
  platform?: Nullable<string>;
  mlsPlatformId?: Nullable<string>;
  deletedAt?: Nullable<string>;
  isNewImport?: boolean;
  status?: Nullable<string>;
  description?: Nullable<string>;
  publicRemarks?: Nullable<string>;
  street?: Nullable<string>;
  city?: Nullable<string>;
  state?: Nullable<string>;
  postalCode?: Nullable<string>;
  listingKey?: Nullable<string>;
  mlsNumber?: Nullable<string>;
  matrixUniqueId?: Nullable<string>;
  propertyType?: Nullable<string>;
  propertySubType?: Nullable<string>;
  mlsStatus?: Nullable<string>;
  listingAgreement?: Nullable<string>;
  listingContractDate?: Nullable<string>;
  onMarketDate?: Nullable<string>;
  offMarketDate?: Nullable<string>;
  closeDate?: Nullable<string>;
  expirationDate?: Nullable<string>;
  originalEntryTimestamp?: Nullable<string>;
  modificationTimestamp?: Nullable<string>;
  originalListPrice?: Nullable<number>;
  closePrice?: Nullable<number>;
  pricePerSquareFoot?: Nullable<number>;
  streetNumber?: Nullable<string>;
  streetName?: Nullable<string>;
  streetSuffix?: Nullable<string>;
  streetDirSuffix?: Nullable<string>;
  unitNumber?: Nullable<string>;
  stateOrProvince?: Nullable<string>;
  county?: Nullable<string>;
  unparsedAddress?: Nullable<string>;
  latitude?: Nullable<number>;
  longitude?: Nullable<number>;
  mlsAreaMajor?: Nullable<string>;
  mlsAreaMinor?: Nullable<string>;
  subdivisionName?: Nullable<string>;
  townshipRange?: Nullable<string>;
  parcelNumber?: Nullable<string>;
  township?: Nullable<string>;
  bedsTotal?: Nullable<number>;
  bathroomsTotalInteger?: Nullable<number>;
  bathroomsFull?: Nullable<number>;
  bathroomsHalf?: Nullable<number>;
  bathroomsOneQuarter?: Nullable<number>;
  bathroomsThreeQuarter?: Nullable<number>;
  livingArea?: Nullable<number>;
  aboveGradeFinishedArea?: Nullable<number>;
  belowGradeFinishedArea?: Nullable<number>;
  lotSizeSquareFeet?: Nullable<number>;
  lotSizeDimensions?: Nullable<string>;
  yearBuiltEffective?: Nullable<number>;
  storiesTotal?: Nullable<number>;
  levelsOrStories?: Nullable<string>;
  belowGradeUnfinishedArea?: Nullable<number>;
  buildingAreaTotal?: Nullable<number>;
  taxAnnualAmount?: Nullable<number>;
  taxYear?: Nullable<number>;
  taxLot?: Nullable<string>;
  taxBlock?: Nullable<string>;
  elementarySchool?: Nullable<string>;
  middleOrJuniorSchool?: Nullable<string>;
  highSchool?: Nullable<string>;
  schoolDistrict?: Nullable<string>;
  daysOnMarket?: Nullable<number>;
  cumulativeDaysOnMarket?: Nullable<number>;
  majorChangeTimestamp?: Nullable<string>;
  majorChangeType?: Nullable<string>;
  listAgentFullName?: Nullable<string>;
  listAgentMlsId?: Nullable<string>;
  listAgentEmail?: Nullable<string>;
  listAgentDirectPhone?: Nullable<string>;
  listOfficeName?: Nullable<string>;
  listOfficeMlsId?: Nullable<string>;
  listOfficePhone?: Nullable<string>;
  coListAgentFullName?: Nullable<string>;
  coListAgentMlsId?: Nullable<string>;
  buyerAgentFullName?: Nullable<string>;
  buyerAgentMlsId?: Nullable<string>;
  buyerOfficeName?: Nullable<string>;
  photosCount?: Nullable<number>;
  photosChangeTimestamp?: Nullable<string>;
  virtualTourUrlUnbranded?: Nullable<string>;
  documentsCount?: Nullable<number>;
  numberOfUnitsTotal?: Nullable<number>;
  agentId?: Nullable<string>;
  createdBy?: Nullable<string>;
  updatedBy?: Nullable<string>;
  bedrooms?: Nullable<number>;
  bathsFull?: Nullable<number>;
  bathsHalf?: Nullable<number>;
  stories?: Nullable<number>;
  fireplaces?: Nullable<number>;
  area?: Nullable<number>;
  location?: Nullable<PropertyLocation>;
  areaPolygon?: Nullable<unknown>;
  lotSizeAcres?: Nullable<number>;
  yearBuilt?: Nullable<number>;
  taxAmount?: Nullable<number>;
  taxFrequency?: Nullable<string>;
  leaseType?: Nullable<string>;
  rejectionReason?: Nullable<string>;
  createdAt?: string;
  updatedAt?: string;
  association?: Nullable<PropertyAssociation>;
  features?: Nullable<PropertyFeatures>;
  propertyRemarks?: Nullable<PropertyRemarks>;
  category?: Nullable<PropertyCategory>;
  subCategory?: Nullable<PropertyCategory>;
  mlsPlatform?: Nullable<PropertyMlsPlatform>;
  photos?: PropertyPhoto[];
  title?: Nullable<string>;
  keyFeatures?: string[];
  roomsTotal?: Nullable<number>;
  numberOfRooms?: Nullable<number>;
  openHouse?: Nullable<PropertyOpenHouse>;
  openHouses?: PropertyOpenHouse[];
  dealerId?: Nullable<string>;
  dealerName?: Nullable<string>;
  dealer?: LegacyAgent;
  agent?: LegacyAgent;
}

export interface DealerSettings {
  id: string;
  financeAppLink?: string;
  financeAppMessage?: string;
  googleLink?: string;
  financeSmsSendActive?: boolean;
  dealershipWebsite?: string;
  dealershipEmail?: string;
  dealershipPhone?: string;
  dealershipAddress?: string;
  dealershipHours?: string;
  dealershipSpecialOffers?: string;
  paperSizeId?: string;
  paperSize?: {
    id: string;
    name: string;
    width: number;
    height: number;
    description?: string;
  };
  promotionalOfferId?: string;
  promotionalOffer?: {
    id: string;
    name: string;
    code: string;
    description?: string;
  };
}

// interface FinancingOffer {
//   apr: number;
//   description: string;
//   disclaimer: string;
// }

interface PropertyDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  config: WidgetConfig;
  selectedCarData: any;
  updateLocalState(newState: any): void;
}

interface QuickOption {
  icon: string;
  title: string;
  shortTitle: string;
  code: NewLeadQuickOption;
  onboardingDialogDetails?: {
    title: string;
    description?: string;
    imagePath?: string;
    submitButtonText: string;
    footerText?: string;
  };
  infoDialogDetails?: {
    title: string;
    description: string;
    imagePath?: string;
    featureTitle?: string;
    featureDescription?: string;
    features: string[];
    submitButtonText: string;
    footerText?: string;
  };
}

const actionButtons: QuickOption[] = [
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/ic_test_drive.png',
    title: 'Schedule Showing',
    shortTitle: 'Book Showing',
    code: 'schedule_test_drive',
    onboardingDialogDetails: {
      title: 'Request a Showing',
      description: 'Choose a time and we’ll have the property ready.',
      submitButtonText: 'Request Showing',
      footerText: 'A real estate specialist will confirm your showing shortly.',
      imagePath:
        'https://miles-cdn.qrbuddy.ai/static/autospotlight/img_request_test_drive.png',
    },
  },
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/ic_payments.png',
    title: 'Price & Payments',
    shortTitle: 'Request Payment',
    code: 'price_and_payments',
    onboardingDialogDetails: {
      title: 'Get Real Payments',
      description:
        'We’ll connect you instantly with a real estate specialist for accurate monthly payments.',
      submitButtonText: 'Submit & Get Payments',
      footerText: 'No credit impact • Fast response • Secure',
    },
  },
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/ic_value_your_trade.png',
    title: 'Property Valuation',
    shortTitle: 'Valuation',
    code: 'value_trade',
    onboardingDialogDetails: {
      title: 'Property Valuation',
      description: 'Request a property valuation from the real estate team.',
      submitButtonText: 'Request Valuation',
      footerText: 'A real estate specialist will be in touch with you shortly.',
      imagePath:
        'https://miles-cdn.qrbuddy.ai/static/autospotlight/img_vehicle_trade_in.png',
    },
    infoDialogDetails: {
      title: 'Property Valuation',
      description: 'Get a valuation request reviewed by the real estate team.',
      submitButtonText: 'Start Valuation Request',
      footerText: 'Valuation details are reviewed directly by the real estate team.',
      imagePath:
        'https://miles-cdn.qrbuddy.ai/static/autospotlight/img_vehicle_trade_in.png',
      features: [
        'Agent-managed valuation review',
        'Fast & secure process',
        'No obligation',
        'Information sent directly to the agent',
      ],
    },
  },
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/ic_finance_app_new.png',
    title: 'Finance App',
    shortTitle: 'Finance\nApp',
    code: 'request_finance',
    onboardingDialogDetails: {
      title: 'Finance Application',
      imagePath:
        'https://miles-cdn.qrbuddy.ai/static/autospotlight/img_finance_application.png',
      submitButtonText: 'Continue',
    },
    infoDialogDetails: {
      title: 'Finance Application',
      description: 'Securely request financing information.',
      imagePath:
        'https://miles-cdn.qrbuddy.ai/static/autospotlight/img_finance_application.png',
      submitButtonText: 'Open Finance Application',
      featureDescription:
        'Complete the secure finance request to explore available financing options.',
      features: ['Fast & secure', 'No obligation', 'Agent-managed'],
      footerText:
        'Finance applications are handled directly by the real estate team.',
    },
  },
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/ic_virtual_walkaround.png',
    title: 'Virtual Tour',
    shortTitle: 'Virtual Tour',
    code: 'virtual_walkaround',
    onboardingDialogDetails: {
      title: 'Virtual Tour Request',
      description:
        'We’ll connect you with a real estate specialist for a live walkthrough of this property.',
      imagePath:
        'https://miles-cdn.qrbuddy.ai/static/autospotlight/img_virtual_walkaround.png',
      submitButtonText: 'Submit Virtual Tour Request',
    },
    infoDialogDetails: {
      title: 'Virtual Tour',
      description:
        'Get a live, personalized walkthrough of this property from a real estate specialist.',
      imagePath:
        'https://miles-cdn.qrbuddy.ai/static/autospotlight/img_virtual_walkaround.png',
      submitButtonText: 'Finalize Virtual Tour Request',
      featureTitle: 'What to Expect',
      features: [
        'Live video walkthrough of the property',
        'Real-time answers to your questions',
        'No pressure, no obligation',
        'Available from anywhere',
      ],
    },
  },
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/ic_vehicle_history.png',
    title: 'Property History Report',
    shortTitle: 'Property History',
    code: 'history',
    onboardingDialogDetails: {
      title: 'Property History Report',
      description: 'Get a report on the property history.',
      imagePath:
        'https://miles-cdn.qrbuddy.ai/static/autospotlight/image_vehicle_history_report.png',
      submitButtonText: 'Request Property History Report',
      footerText: 'A real estate specialist will be in touch with you shortly.',
    },
    infoDialogDetails: {
      title: 'Property History Report',
      description: 'Request the official history report for this property.',
      imagePath:
        'https://miles-cdn.qrbuddy.ai/static/autospotlight/image_vehicle_history_report.png',
      submitButtonText: 'Request Property History Report',
      features: [
        'Ownership and title history',
        'Property records',
        'Agent-verified report',
      ],
      footerText: 'Your request will be securely sent to the agent.',
    },
  },
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/ic_similar_cars.png',
    title: 'Similar Properties',
    shortTitle: 'Similar Properties',
    code: 'similar_properties',
  },
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/ic_contact_dealer.png',
    title: 'Contact Agent',
    shortTitle: 'Contact Agent',
    code: 'contact_dealer',
  },
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/ic_share_vehicle.png',
    title: 'Share Property',
    shortTitle: 'share',
    code: 'share_property',
  },
];

const PropertyDetailsDialog: React.FC<PropertyDetailsDialogProps> = ({
  open,
  onClose,
  config,
  selectedCarData,
  updateLocalState,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [api] = useState(() => new WidgetAPI(config));

  const [localLoading, setLocalLoading] = useState(false);
  const [propertyId, setPropertyId] = useState<string | undefined>(
    // '822f9d00-e268-11f0-9838-09728599971c', // Local
    // 'a0d514e3-d185-11f0-8b4b-8b061a999436', // Prod
    selectedCarData?.id,
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [propertyData, setPropertyData] = useState<PropertyResponse | null>(null);
  const [dealerSettingsData, setDealerSettingsData] = useState<any>(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);
  const fetchingRef = useRef(false);

  const data = propertyData;

  const description =
    data?.description ||
    data?.publicRemarks ||
    data?.propertyRemarks?.publicRemarks ||
    '';
  // const dealerName = data?.dealer?.dealerNames?.[0] || data?.dealerName || '';
  const words = description?.trim() ? description?.split(/\s+/) : [];
  const isLong = words.length > 50;
  const displayedText = expanded ? description : words?.slice(0, 50)?.join(' ');
	  const photoSource = [...(data?.photos || [])];
  // const photoItems =
  //   photoSource
  //     ?.map((photo: any) => getAssetURL(photo.key, photo?.url))
  //     ?.filter((url): url is string => Boolean(url)) || [];

  // const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // const financingOffer: FinancingOffer = {
  //   apr: 1.99,
  //   description: 'Financing Available',
  //   disclaimer: 'For qualified buyers • Limited time offer',
  // };

  const [newLeadStep, setNewLeadStep] = useState<NewLeadDialogStep | undefined>(
    undefined,
  );
  const [newLeadOption, setNewLeadOption] = useState<QuickOption | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [requestId, setRequestId] = useState<string>('');
  const [onboardingData, setOnboardingData] = useState<any>(undefined);
  const [timeLeft, setTimeLeft] = useState(60);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [downPaymentAmount, setDownPaymentAmount] = useState<number | null>(
    null,
  );
  const [downPaymentPercent, setDownPaymentPercent] = useState(10);
  const [interestRate, setInterestRate] = useState(6.8);
  const [intentScore, setIntentScore] = useState(0);
  const [isLeadActionLoading, setIsLeadActionLoading] =
    useState<boolean>(false);
  // const [financeResponse, setFinanceResponse] = useState<{
  //   url: string;
  //   message: string;
  // } | null>(null);

  const {
    toggle: keyFeaturesToggle,
    onOpen: keyFeaturesOnOpen,
    onClose: keyFeaturesOnClose,
  } = useToggle();

  const {
    toggle: newLeadCaptureToggle,
    onOpen: newLeadCaptureOnOpen,
    onClose: newLeadCaptureOnClose,
  } = useToggle();

  const {
    toggle: similarCarsToggle,
    onOpen: similarCarsOnOpen,
    onClose: similarCarsOnClose,
  } = useToggle();

  const {
    toggle: contactDealerToggle,
    onOpen: contactDealerOnOpen,
    onClose: contactDealerOnClose,
  } = useToggle();

  useEffect(() => {
    const fetchCar = async () => {
      // Prevent duplicate calls
      if (fetchingRef.current || !propertyId || propertyData) return;

      fetchingRef.current = true;
      setLocalLoading(true);
      try {
        const resultAction = await api.getCar(propertyId);

        if (resultAction.id) {
          setPropertyData(resultAction);
          setDealerSettingsData(
            resultAction?.dealer?.dealerSettings ||
            resultAction?.agent?.agentSettings ||
            resultAction?.agent?.dealerSettings,
          );

          // Make guest/ghost user entry
          if (!api.isAuthenticated()) {
            const authToken = getAuthToken();
            let anonymousAuthToken = getAnonymousToken();
            if (
              IsNullOrEmpty(authToken) &&
              (IsNullOrEmpty(anonymousAuthToken) ||
                isAuthTokenExpired(anonymousAuthToken))
            ) {
              await api.anonymousLogin();
            }

            let guestConversationId = api.getConversationId();

            if (!guestConversationId) {
              try {
                const result = await api.createGuestConversation();
                guestConversationId = result.conversationId;
                api.setGuestConversation(guestConversationId);

                updateLocalState({ conversationId: guestConversationId, isAnonymousAccess: true });
              } catch (error) {
                console.error('Failed to create SAGE conversation:', error);
              }
            }

            api.createUserSession().then(() => {
              reportUserInteraction(InteractionType.cover, resultAction.id);
            });
          }

          api.handleLikeProperty(resultAction.id);
        }
      } catch (err) {
        setError('Something went wrong');
      } finally {
        setLocalLoading(false);
        fetchingRef.current = false;
      }
    };

    fetchCar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, propertyData, api]);

  const handleSubmitOnboarding = async (leadData: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    timezone?: string;
    tcpaConsent: boolean;
  }) => {
    try {
      if (!leadData.email.trim()) {
        // Remove all non-numeric characters for storage and API
        const phoneDigits = leadData.phone.replace(/\D/g, '');

        // Auto-generate email from phone number
        leadData.email = `${phoneDigits}@qrbuddy.ai`;
      }

      const timezone = getUserTimezone();
      if (timezone && timezone !== 'UTC') {
        leadData.timezone = timezone;
      }

      const _data = {
        ...leadData,
        countryCode: '+1',
        agentId: data?.agentId || data?.dealerId || config.agentId || '',
        guestConversationId: localStorage.getItem('guestConversationId'),
      };
      setOnboardingData(_data);

      handleOtpSend(_data);
    } catch (error: any) {
      console.error('Failed to submit onboarding:', error);
      toast.error(error.message || 'Failed to submit. Please try again.');
    }
  };

  const handleVerifyOTP = async (otp: string) => {
    setIsLoading(true);

    try {
      const result = await api.verifyOTP(requestId, otp);

      if (result?.accessToken) {
        // Set authenticating flag to prevent loadConversationHistory from running
        // setIsAuthenticating(true);
        const newConversationId = result.conversationId || null;

        api.setAuth(
          result.accessToken,
          newConversationId || undefined,
          result.user,
        );

        // localStorage.setItem(config.AUTH_KEY, response?.accessToken);

        // if (window.MilesAIWidget?.destroy) {
        //   window.MilesAIWidget?.destroy();
        // }

        // if (window.MilesAIWidgetSetStorage) {
        //   window.MilesAIWidgetSetStorage({
        //     authToken: response?.accessToken,
        //     userData: response?.user,
        //     dealerId: data?.dealerId,
        //     isWelcome: false,
        //     isShowEnhanceView: false,
        //     isWebAppUser: true,
        //   });
        // }

        // if (window.MilesAIWidgetInit) {
        //   window.MilesAIWidgetInit({
        //     dealerId: data?.dealerId,
        //     dealerName: dealerName,
        //     isWelcome: false,
        //   });

        //   setTimeout(() => {
        //     if (window.MilesAIWidget?.setView) {
        //       window.MilesAIWidget?.setView('chat');
        //     }
        //   }, 1000);
        // }

        // scanCar();

        if (newLeadOption?.code === 'schedule_test_drive') {
          setNewLeadStep('confirmation');
        } else if (newLeadOption?.code === 'request_finance') {
          setNewLeadStep('confirmation');
          handleRequestActionClick(LeadActionType.requestFinance);
        } else if (newLeadOption?.code === 'price_and_payments') {
          handleGetPaymentsRequest();
          closeOnboardingForm();
        } else if (newLeadOption?.code === 'history') {
          handleRequestActionClick(LeadActionType.requestVehicleHistory);
          closeOnboardingForm();
        } else if (newLeadOption?.code === 'live_agent') {
          handleRequestActionClick(LeadActionType.requestLiveAgent);
          closeOnboardingForm();
        } else if (newLeadOption?.code === 'value_trade') {
          handleRequestActionClick(LeadActionType.requestVehicleTradeIn);
          closeOnboardingForm();
        } else if (newLeadOption?.code === 'virtual_walkaround') {
          handleRequestActionClick(LeadActionType.requestVirtualWalkaround);
          closeOnboardingForm();
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSend = async (data: any) => {
    try {
      setIsLoading(true);
      const result = await api.sendIdentity({ ...data });

      const expiryTimestamp = Date.now() + 60 * 1000;
      localStorage.setItem('otpExpiry', expiryTimestamp.toString());

      if (result.accessToken) {
      } else if (result.requestId) {
        setRequestId(result.requestId);
        setIsResendDisabled(true);
        setNewLeadStep('otp');
        setTimeLeft(60);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = () => {
    if (isResendDisabled) return;
    setIsResendDisabled(true);
    setTimeLeft(60);
    handleOtpSend(onboardingData);
  };

  const handleQuickOptionClicked = (option?: QuickOption, downPayment = -1) => {
    setNewLeadOption(option);

    switch (option?.code) {
      case 'schedule_test_drive':
      case 'value_trade':
      case 'virtual_walkaround':
      case 'price_and_payments':
      case 'history':
      case 'live_agent':
        // case 'request_finance':
        if (api.isAuthenticated()) {
          if (
            newLeadStep !== 'information' &&
            [
              'value_trade',
              'virtual_walkaround',
              'history',
              'price_and_payments',
            ].includes(option?.code)
          ) {
            reportUserInteraction(getInteractionTypeFromQuickOption(option.code))
            setNewLeadStep('information');
            newLeadCaptureOnOpen();
            return;
          } else if (
            [
              'value_trade',
              'virtual_walkaround',
              'history',
              'price_and_payments',
            ].includes(option?.code)
          ) {
            closeOnboardingForm();
          }

          if (option?.code === 'schedule_test_drive') {
            setNewLeadStep('confirmation');
            newLeadCaptureOnOpen();
            // reportUserInteraction(getInteractionTypeFromQuickOption(option.code))
            // } else if (option?.code === 'request_finance') {
            //   if (newLeadStep !== 'confirmation') {
            //     handleRequestActionClick(LeadActionType.requestFinance);
            //     setNewLeadStep('confirmation');
            //     newLeadCaptureOnOpen();
            //   } else {
            //     window.open(financeResponse?.url, '_blank');
            //     closeOnboardingForm();
            //   }
          } else if (option?.code === 'price_and_payments') {
            handleGetPaymentsRequest(downPayment);
          } else if (option?.code === 'history') {
            handleRequestActionClick(LeadActionType.requestVehicleHistory);
          } else if (option?.code === 'value_trade') {
            handleRequestActionClick(LeadActionType.requestVehicleTradeIn);
          } else if (option?.code === 'virtual_walkaround') {
            handleRequestActionClick(LeadActionType.requestVirtualWalkaround);
          } else {
            handleRequestActionClick(LeadActionType.requestLiveAgent);
          }
        } else {
          if (
            newLeadStep !== 'information' &&
            [
              'value_trade',
              'virtual_walkaround',
              'history',
              'price_and_payments',
            ].includes(option?.code)
          ) {
            reportUserInteraction(getInteractionTypeFromQuickOption(option.code))
            setNewLeadStep('information');
          } else {
            reportUserInteraction(getInteractionTypeFromQuickOption(option.code))
            setNewLeadStep('onboarding');
          }
          newLeadCaptureOnOpen();
        }
        break;
      case 'request_finance':
        if (api.isAuthenticated()) {
          handleRequestActionClick(LeadActionType.requestFinance);
        }
        if (newLeadStep !== 'confirmation') {
          reportUserInteraction(getInteractionTypeFromQuickOption(option.code))
          setNewLeadStep('confirmation');
          newLeadCaptureOnOpen();
        } else {
          gotoDealershipFinanceAppPage();
          closeOnboardingForm();
        }
        break;
      case 'share_property':
        reportUserInteraction(getInteractionTypeFromQuickOption(option.code))
        newLeadCaptureOnOpen();
        break;
    }
  };

  const handleRequestActionClick = async (actionType: LeadActionType) => {
    try {
      setIsLeadActionLoading(true);
      const response = await api.requestLeadAction({
        propertyId: data?.id ?? '',
        actionType: actionType,
        scheduleDate: null,
      });

      if (response) {
        if (actionType === LeadActionType.requestFinance) {
          // setFinanceResponse({
          //   url: response?.url,
          //   message: response?.message,
          // });
        } else {
          toast.success(
            'Information submitted successfully. An agent will be in touch shortly.',
          );
        }
      }
    } catch (error) {
      console.error('Error occurred while requesting action:', error);
    } finally {
      setIsLeadActionLoading(false);
    }
  };

  const handleGetPaymentsRequest = async (downPayment = -1) => {
    try {
      const payload = {
        propertyId: data?.id ?? '',
        actionType: LeadActionType.requestGetPayments,
        downPaymentAmount: downPayment >= 0 ? downPayment : downPaymentAmount,
      };

      closeOnboardingForm();

      const result = await api.requestLeadAction(payload);

      if (result) {
        toast.success(
          'Information submitted successfully. An agent will be in touch shortly.',
        );
      }
    } catch (e) {
      console.error('Error occurred while requesting get payments:', error);
    }
  };

  const getInteractionTypeFromQuickOption = (
    option?: NewLeadQuickOption,
  ): InteractionType | undefined => {
    if (!option) return undefined;
    return INTERACTION_TYPE_BY_QUICK_OPTION[option];
  };

  const reportUserInteraction = (interactionType?: InteractionType, propertyId?: string) => {
    if (interactionType) {
      api.createUserInteraction({
        actionType: interactionType.toString(),
        propertyId: propertyId ?? data?.id ?? ''
      }).then((result: any) => {
        if (result?.intentScore !== undefined && !Number.isNaN(Number(result.intentScore))) {
          setIntentScore(Number(result.intentScore));
        }
      }).catch(() => undefined);
    }
  }

  const closeOnboardingForm = () => {
    newLeadCaptureOnClose();
    setOnboardingData(undefined);
    setNewLeadStep(undefined);
    setNewLeadOption(undefined);
  };

  // const handlePrevImage = () => {
  //   if (propertyData?.photos) {
  //     setCurrentImageIndex((prev) =>
  //       prev === 0 ? propertyData.photos.length - 1 : prev - 1,
  //     );
  //   }
  // };

  // const handleNextImage = () => {
  //   if (propertyData?.photos) {
  //     setCurrentImageIndex((prev) =>
  //       prev === propertyData.photos.length - 1 ? 0 : prev + 1,
  //     );
  //   }
  // };

  const gotoDealershipWebsite = () => {
    if (IsNotNullOrEmpty(dealerSettingsData?.dealershipWebsite)) {
      window.open(dealerSettingsData?.dealershipWebsite, '_blank');
    }
  };

  const gotoDealershipFinanceAppPage = () => {
    if (IsNotNullOrEmpty(dealerSettingsData?.financeAppLink)) {
      window.open(dealerSettingsData?.financeAppLink, '_blank');
    } else {
      toast.error('The finance application link has not been added by the agent.');
    }
  };

  // const gotoDealershipGoogleReviewPage = () => {
  //   if (IsNotNullOrEmpty(dealerSettingsData?.googleLink)) {
  //     window.open(dealerSettingsData?.googleLink, '_blank');
  //   }
  // };

  const handleOpenLocation = () => {
    const url = `https://www.google.com/maps/search/?api=1&query=${getEncodeFullAddress()}`;
    window.open(url, '_blank');
  };

  const getEncodeFullAddress = (): string => {
    return encodeURIComponent(
      address || dealerSettingsData?.dealershipAddress || '',
    );
  };

  const handleCopyLink = (shareLink?: string) => {
    if (!shareLink) {
      toast.error('No link to copy.');
      return;
    }

    if (!navigator.clipboard?.writeText) {
      toast.error('Copy not supported in this browser.');
      return;
    }

    navigator.clipboard
      .writeText(shareLink)
      .then(() => toast.success('Copied to clipboard'))
      .catch(() => toast.error('Failed to copy link.'));
  };

  const scrollLeft = () => {
    let newIndex = currentIndex - 1;
    if (newIndex < 0) {
      newIndex = newIndex = photoSource?.length - 1;
    }
    setCurrentIndex(newIndex);
    scrollToIndex(newIndex);
    reportUserInteraction(InteractionType.photos);
  };

  const scrollRight = () => {
    let newIndex = currentIndex + 1;
    if (newIndex >= photoSource?.length) {
      newIndex = 0;
    }
    setCurrentIndex(newIndex);
    scrollToIndex(newIndex);
    reportUserInteraction(InteractionType.photos);
  };

  const scrollToIndex = (index: number) => {
    const el = scrollRef?.current;
    if (!el) return;

    const childWidth =
      el?.firstChild instanceof HTMLElement
        ? el?.firstChild?.offsetWidth + 16
        : 300;

    el?.scrollTo({ left: index * childWidth, behavior: 'smooth' });
  };

  useEffect(() => {
    const savedExpiry = localStorage.getItem('otpExpiry');
    const now = Date.now();

    if (!savedExpiry) return;

    if (parseInt(savedExpiry) > now) {
      const remaining = Math.floor((parseInt(savedExpiry) - now) / 1000);
      setTimeLeft(remaining);
      setIsResendDisabled(true);
    } else {
      setTimeLeft(0);
      setIsResendDisabled(false);
    }
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(timer);
  }, [timeLeft]);

  const listPrice = Number(data?.listPrice ?? data?.price ?? 0);
  const agentName =
    data?.listAgentFullName ||
    [data?.dealer?.firstName, data?.dealer?.lastName].filter(Boolean).join(' ') ||
    config.agentName ||
    'Agent';
  const brokerageName =
    data?.listOfficeName ||
    data?.dealer?.dealerNames?.[0] ||
    data?.dealerName ||
    config.agentName ||
    '';
  const address =
    data?.unparsedAddress ||
    [data?.street, data?.city, data?.state || data?.stateOrProvince, data?.postalCode]
      .filter(Boolean)
      .join(', ') ||
    data?.title ||
    'Property';
  const daysOnMarket =
    data?.daysOnMarket ||
    (data?.onMarketDate
      ? Math.max(
        0,
        Math.floor(
          (Date.now() - new Date(data.onMarketDate).getTime()) /
          (1000 * 60 * 60 * 24),
        ),
      )
      : null);

  const formatCurrency = (value?: string | number | null) => {
    const amount = Number(value ?? 0);
    if (!Number.isFinite(amount) || amount <= 0) return 'Contact for price';
    return `$${Math.round(amount).toLocaleString()}`;
  };

  const formatNumber = (value?: string | number | null) => {
    const amount = Number(value ?? 0);
    if (!Number.isFinite(amount) || amount <= 0) return '-';
    return Math.round(amount).toLocaleString();
  };

  const formatDate = (value?: string | Date | null) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatMonthYear = (value?: string | Date | null) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const getAgentInitials = () => {
    const name = agentName || brokerageName || 'Agent';
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part: string) => part[0]?.toUpperCase())
      .join('') || 'A';
  };

  const toList = (value: any): string[] => {
    if (Array.isArray(value)) return value.filter(Boolean).map((item) => String(item));
    if (IsNotNullOrEmpty(value)) return [String(value)];
    return [];
  };

  const getPhotoUrl = (index: number) =>
    photoSource[index]
      ? getAssetURL(
        config.assetsUrl,
        photoSource[index]?.key,
        photoSource[index]?.url,
      )
      : '';

  const getAction = (code: NewLeadQuickOption) =>
    actionButtons.find((item) => item.code === code);

  const renderDialogHeader = () => (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        height: 56,
        px: 1.25,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: '#0C1E3C',
        color: '#fff',
        boxShadow: '0 8px 26px rgba(12, 30, 60, 0.18)',
      }}
    >
      <IconButton onClick={onClose} aria-label='Close property details' sx={{ color: '#fff' }}>
        <KeyboardBackspace />
      </IconButton>
      <Box sx={{ minWidth: 0, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700 }} noWrap>
          {agentName}
        </Typography>
        <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase' }} noWrap>
          {brokerageName || 'QR Buddy Realty'}
        </Typography>
      </Box>
      <Button
        onClick={() => handleQuickOptionClicked(getAction('contact_dealer'))}
        sx={{
          minWidth: 0,
          px: 1.25,
          py: 0.65,
          borderRadius: 5,
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'none',
          bgcolor: '#C9A84C',
          color: '#0C1E3C',
          '&:hover': { bgcolor: '#E8C46A' },
        }}
      >
        Message
      </Button>
    </Box>
  );

  const renderHeroSection = () => {
    const currentPhotoUrl = getPhotoUrl(currentIndex);

    return (
      <Box
        onClick={() => {
          if (photoSource.length > 0) reportUserInteraction(InteractionType.photos);
        }}
        sx={{
          position: 'relative',
          height: { xs: 292, sm: 360 },
          overflow: 'hidden',
          cursor: photoSource.length > 0 ? 'pointer' : 'default',
          bgcolor: '#0C1E3C',
        }}
      >
        {currentPhotoUrl ? (
          <Box
            component='img'
            src={currentPhotoUrl}
            alt={address}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center 35%',
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              color: 'rgba(255,255,255,0.7)',
              background: 'linear-gradient(135deg, #0C1E3C 0%, #1E3A5F 100%)',
            }}
          >
            <HomeWorkIcon sx={{ fontSize: 52, color: '#C9A84C' }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
              Property photo unavailable
            </Typography>
          </Box>
        )}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(to top, rgba(13,31,60,0.92) 0%, rgba(13,31,60,0.2) 58%, rgba(13,31,60,0.04) 100%)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          bgcolor: '#C9A84C',
          color: '#0C1E3C',
          fontSize: 10,
          fontWeight: 800,
          px: 1.2,
          py: 0.5,
          borderRadius: 5,
          textTransform: 'uppercase',
        }}
      >
        {data?.mlsStatus || data?.status || 'Active'}
      </Box>
      {(data?.mlsNumber || data?.listingId) && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            bgcolor: 'rgba(13,31,60,0.8)',
            color: '#fff',
            fontSize: 10,
            px: 1.2,
            py: 0.5,
            borderRadius: 5,
          }}
        >
          MLS: {data?.mlsNumber || data?.listingId}
        </Box>
      )}
      {photoSource.length > 1 && (
        <>
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              scrollLeft();
            }}
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.82)',
              '&:hover': { bgcolor: '#fff' },
            }}
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              scrollRight();
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.82)',
              '&:hover': { bgcolor: '#fff' },
            }}
          >
            <ChevronRight />
          </IconButton>
        </>
      )}
      <Box sx={{ position: 'absolute', left: 14, right: 14, bottom: 14 }}>
        {daysOnMarket !== null && (
          <Box
            sx={{
              display: 'inline-flex',
              mb: 1,
              px: 1.2,
              py: 0.4,
              borderRadius: 5,
              border: '1px solid rgba(255,255,255,0.22)',
              bgcolor: 'rgba(255,255,255,0.12)',
            }}
          >
            <Typography sx={{ color: '#fff', fontSize: 10 }}>
              On market: <Box component='span' sx={{ color: '#C9A84C', fontWeight: 800 }}>{daysOnMarket}</Box> days
            </Typography>
          </Box>
        )}
        <Typography
          sx={{
            fontFamily: '"Playfair Display", Georgia, serif',
            fontSize: { xs: 34, sm: 42 },
            fontWeight: 800,
            color: '#fff',
            lineHeight: 1,
            textShadow: '0 2px 12px rgba(0,0,0,0.35)',
          }}
        >
          {formatCurrency(listPrice)}
        </Typography>
        <Typography sx={{ mt: 0.6, color: 'rgba(255,255,255,0.82)', fontSize: 12.5 }}>
          {address}
        </Typography>
      </Box>
      {photoSource.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            right: 14,
            bottom: 14,
            color: '#fff',
            bgcolor: 'rgba(0,0,0,0.55)',
            fontSize: 11,
            px: 1.2,
            py: 0.5,
            borderRadius: 5,
          }}
        >
          {currentIndex + 1} / {photoSource.length}
        </Box>
      )}
      </Box>
    );
  };

  const renderStatsBar = () => {
    const stats = [
      { value: data?.bedsTotal || data?.bedrooms || '-', label: 'Beds' },
      { value: data?.bathroomsTotalInteger || data?.bathroomsFull || '-', label: 'Baths' },
      { value: formatNumber(data?.livingArea || data?.area), label: 'Sq Ft' },
      { value: data?.lotSizeAcres || '-', label: 'Acres' },
      { value: data?.yearBuilt || '-', label: 'Built' },
    ];

    return (
      <Box sx={{ bgcolor: '#0C1E3C', display: 'flex', py: 1.1 }}>
        {stats.map((stat, index) => (
          <Box
            key={stat.label}
            sx={{
              flex: 1,
              textAlign: 'center',
              borderRight: index < stats.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
              px: 0.4,
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontSize: { xs: 15, sm: 18 },
                fontWeight: 800,
                color: '#C9A84C',
                lineHeight: 1.1,
              }}
            >
              {stat.value}
            </Typography>
            <Typography sx={{ mt: 0.3, fontSize: 8.5, color: 'rgba(255,255,255,0.52)', textTransform: 'uppercase' }}>
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const renderActionCards = () => {
    const actions = [
      {
        label: 'Photos',
        meta: `${photoSource.length || 0}`,
        icon: <PhotoLibraryIcon sx={{ fontSize: 28 }} />,
        onClick: () => reportUserInteraction(InteractionType.photos),
      },
      {
        label: '3D Tour',
        meta: data?.virtualTourUrlUnbranded ? 'View' : 'Ask',
        icon: <HomeWorkIcon sx={{ fontSize: 28 }} />,
        onClick: () => {
          if (data?.virtualTourUrlUnbranded) {
            window.open(data.virtualTourUrlUnbranded, '_blank');
            reportUserInteraction(InteractionType.virtualTour);
          } else {
            handleQuickOptionClicked(getAction('virtual_walkaround'));
          }
        },
      },
      {
        label: 'Book Tour',
        meta: 'Fast',
        icon: <CalendarMonthIcon sx={{ fontSize: 28 }} />,
        onClick: () => handleQuickOptionClicked(getAction('schedule_test_drive')),
      },
    ];

    return (
      <Box sx={{ display: 'flex', gap: 0.9, p: 1.2, bgcolor: '#fff', borderBottom: '1px solid #E5E7EB' }}>
        {actions.map((action) => (
          <Box
            key={action.label}
            onClick={action.onClick}
            sx={{
              flex: 1,
              minHeight: 92,
              borderRadius: 2,
              overflow: 'hidden',
              position: 'relative',
              cursor: 'pointer',
              bgcolor: '#0C1E3C',
              color: '#fff',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              '&:active': { transform: 'scale(0.985)' },
            }}
          >
            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #0C1E3C 0%, #1E3A5F 100%)' }} />
            <Box sx={{ position: 'relative', fontSize: 24, lineHeight: 1 }}>{action.icon}</Box>
            <Typography sx={{ position: 'relative', mt: 0.8, fontSize: 12, fontWeight: 800 }}>{action.label}</Typography>
            <Typography sx={{ position: 'relative', mt: 0.2, fontSize: 9, color: 'rgba(255,255,255,0.58)' }}>{action.meta}</Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const renderAgentContactCard = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.2,
        p: 1.4,
        bgcolor: '#0C1E3C',
        borderTop: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <Box
        sx={{
          width: 46,
          height: 46,
          border: '2px solid #C9A84C',
          bgcolor: '#1E3A5F',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#C9A84C',
          fontSize: 17,
          fontWeight: 800,
          flexShrink: 0,
        }}
      >
        {getAgentInitials()}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ color: '#fff', fontSize: 13, fontWeight: 700, lineHeight: 1.2 }} noWrap>
          {agentName || 'Agent Name'}
        </Typography>
        <Typography sx={{ color: '#C9A84C', fontSize: 11, lineHeight: 1, mt: 0.35 }} noWrap>
          5.0 rating
        </Typography>
        <Typography
          sx={{
            color: 'rgba(255,255,255,0.42)',
            fontSize: 9,
            textTransform: 'uppercase',
            letterSpacing: 0.3,
            mt: 0.35,
          }}
          noWrap
        >
          {brokerageName || 'QR Buddy Realty'}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.7 }}>
        <IconButton
          onClick={() => {
            reportUserInteraction(InteractionType.contactAgent);
            handleQuickOptionClicked(getAction('contact_dealer'));
          }}
          aria-label='Message agent'
          sx={{ width: 36, height: 36, bgcolor: '#C9A84C', color: '#0C1E3C', '&:hover': { bgcolor: '#E8C46A' } }}
        >
          <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton
          onClick={() => {
            contactDealerOnOpen();
            reportUserInteraction(InteractionType.contactAgent);
          }}
          aria-label='Call agent'
          sx={{
            width: 36,
            height: 36,
            bgcolor: 'rgba(255,255,255,0.1)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.18)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' },
          }}
        >
          <CallIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );

  const renderOpenHouseBanner = () => {
    const openHouse = data?.openHouse || data?.openHouses?.[0];
    if (!openHouse) return null;

    const dateLabel = [formatDate(openHouse?.date || openHouse?.startDate), openHouse?.time || openHouse?.startTime]
      .filter((item) => item && item !== '-')
      .join(' · ');

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          p: 1.4,
          bgcolor: '#C9A84C',
          color: '#0C1E3C',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, opacity: 0.7 }}>
            Open House
          </Typography>
          <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 18, fontWeight: 800 }} noWrap>
            {dateLabel || 'Upcoming'}
          </Typography>
        </Box>
        <Button
          onClick={() => handleQuickOptionClicked(getAction('schedule_test_drive'))}
          sx={{ color: '#0C1E3C', textDecoration: 'underline', textTransform: 'none', fontSize: 12, fontWeight: 800, minWidth: 'auto' }}
        >
          Book
        </Button>
      </Box>
    );
  };

  const renderPhotoGallery = () => {
    if (!photoSource || photoSource.length === 0) return null;

    return renderSectionCard(
      'Photos',
      <>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
            {photoSource.length} listing photos
          </Typography>
          <Button onClick={() => reportUserInteraction(InteractionType.photos)} sx={{ minWidth: 0, p: 0, fontSize: 12, textTransform: 'none', color: '#C9A84C', fontWeight: 800 }}>
            See all
          </Button>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 0.8,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {photoSource.slice(0, 10).map((photo: any, index: number) => {
            const photoUrl = getAssetURL(config.assetsUrl, photo?.key, photo?.url);
            return (
              <Box
                key={photo?.id || index}
                onClick={() => {
                  setCurrentIndex(index);
                  reportUserInteraction(InteractionType.photos);
                }}
                sx={{ minWidth: 116, height: 86, borderRadius: 2, overflow: 'hidden', cursor: 'pointer', bgcolor: '#0C1E3C' }}
              >
                {photoUrl ? (
                  <Box component='img' src={photoUrl} alt={`Property photo ${index + 1}`} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C9A84C' }}>
                    <HomeWorkIcon />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </>,
      InteractionType.photos,
    );
  };

  const renderIntentScore = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        bgcolor: '#0C1E3C',
        color: '#fff',
        borderTop: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
          Intent Score
        </Typography>
        <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 24, fontWeight: 800, color: '#C9A84C', lineHeight: 1 }}>
          {intentScore.toFixed(1)}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ height: 4, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, mb: 0.7 }}>
          <Box
            sx={{
              height: '100%',
              width: `${Math.max(0, Math.min(intentScore, 10)) * 10}%`,
              bgcolor: '#C9A84C',
              borderRadius: 2,
              transition: 'width 0.5s ease',
            }}
          />
        </Box>
        <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.36)' }}>
          Interactions tracked in real time
        </Typography>
      </Box>
      <Button
        onClick={() => {
          reportUserInteraction(InteractionType.contactAgent);
          handleQuickOptionClicked(getAction('contact_dealer'));
        }}
        sx={{ bgcolor: '#C9A84C', color: '#0C1E3C', borderRadius: 2, px: 1.2, py: 0.8, fontSize: 11, fontWeight: 800, textTransform: 'none', '&:hover': { bgcolor: '#E8C46A' } }}
      >
        Connect
      </Button>
    </Box>
  );

  const renderPaymentCalculator = () => {
    if (!listPrice) return null;

    const loanTerm = 30;
    const downPayment = listPrice * (downPaymentPercent / 100);
    const loanAmount = listPrice - downPayment;
    const monthlyRate = interestRate / 100 / 12;
    const numPayments = loanTerm * 12;
    const principalInterest =
      monthlyRate > 0
        ? (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
        (Math.pow(1 + monthlyRate, numPayments) - 1)
        : loanAmount / numPayments;
    const monthlyTax = Number(data?.taxAnnualAmount || data?.taxAmount || 0) / 12;
    const monthlyHOA = Number(data?.association?.associationFee || 0);
    const totalMonthly = principalInterest + monthlyTax + monthlyHOA;

    return (
      <Card
        sx={{
          borderRadius: 0,
          p: 1.8,
          bgcolor: '#0C1E3C',
          background: 'linear-gradient(135deg, #0C1E3C 0%, #142849 100%)',
          color: '#fff',
          boxShadow: 'none',
        }}
        onClick={() => reportUserInteraction(InteractionType.paymentCalculator)}
      >
        <Typography sx={{ color: 'rgba(255,255,255,0.52)', fontSize: 10, textTransform: 'uppercase' }}>
          Estimated monthly cost
        </Typography>
        <Box sx={{ mt: 0.4, mb: 1.5 }}>
          <Typography component='span' sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 30, fontWeight: 800, color: '#C9A84C' }}>
            ${Math.round(totalMonthly).toLocaleString()}
          </Typography>
          <Typography component='span' sx={{ ml: 0.5, color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
            /mo
          </Typography>
        </Box>
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.7 }}>
            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>Down Payment</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#C9A84C' }}>
              {downPaymentPercent}% · ${Math.round(downPayment).toLocaleString()}
            </Typography>
          </Box>
          <Box
            component='input'
            type='range'
            min='3'
            max='30'
            value={downPaymentPercent}
            onChange={(event: any) => {
              setDownPaymentPercent(Number(event.target.value));
              setDownPaymentAmount(Math.round(listPrice * (Number(event.target.value) / 100)));
              reportUserInteraction(InteractionType.paymentCalculator);
            }}
            sx={{
              width: '100%',
              height: 4,
              borderRadius: 2,
              WebkitAppearance: 'none',
              appearance: 'none',
              background: `linear-gradient(90deg, #C9A84C 0%, #C9A84C ${((downPaymentPercent - 3) / 27) * 100}%, rgba(255,255,255,0.2) ${((downPaymentPercent - 3) / 27) * 100}%)`,
              outline: 'none',
              '&::-webkit-slider-thumb': {
                WebkitAppearance: 'none',
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: '#C9A84C',
                border: '3px solid white',
              },
            }}
          />
        </Box>
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.7 }}>
            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>Interest Rate</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#C9A84C' }}>{interestRate.toFixed(1)}%</Typography>
          </Box>
          <Box
            component='input'
            type='range'
            min='50'
            max='100'
            value={interestRate * 10}
            onChange={(event: any) => setInterestRate(Number(event.target.value) / 10)}
            sx={{
              width: '100%',
              height: 4,
              borderRadius: 2,
              WebkitAppearance: 'none',
              appearance: 'none',
              background: `linear-gradient(90deg, #C9A84C 0%, #C9A84C ${((interestRate * 10 - 50) / 50) * 100}%, rgba(255,255,255,0.2) ${((interestRate * 10 - 50) / 50) * 100}%)`,
              outline: 'none',
              '&::-webkit-slider-thumb': {
                WebkitAppearance: 'none',
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: '#C9A84C',
                border: '3px solid white',
              },
            }}
          />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0.8, mb: 1 }}>
          {[
            { label: 'Principal + Interest', value: Math.round(principalInterest) },
            { label: 'Property Tax', value: Math.round(monthlyTax) },
            { label: 'HOA', value: Math.round(monthlyHOA) },
          ].map((row) => (
            <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: 'rgba(255,255,255,0.07)', borderRadius: 1.5, px: 1, py: 0.8 }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 10, textTransform: 'uppercase' }}>
                {row.label}
              </Typography>
              <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 800 }}>
                ${row.value.toLocaleString()}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, pt: 1, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Box sx={{ width: 6, height: 6, bgcolor: '#16A34A', borderRadius: '50%' }} />
          <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 9 }}>
            Adjusting payment details is tracked as a buyer intent signal.
          </Typography>
        </Box>
      </Card>
    );
  };

  const renderSectionCard = (
    title: string,
    children: React.ReactNode,
    interactionType?: InteractionType,
  ) => (
    <Card
      onClick={() => interactionType && reportUserInteraction(interactionType)}
      sx={{
        borderRadius: 0,
        boxShadow: 'none',
        p: 1.8,
        borderTop: '1px solid #E5E7EB',
        cursor: interactionType ? 'pointer' : 'default',
      }}
    >
      <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#1A1A2E', mb: 1.5 }}>
        {title}
      </Typography>
      {children}
    </Card>
  );

  const renderDescription = () => {
    if (!description) return null;
    return renderSectionCard(
      'Property Description',
      <Typography sx={{ fontSize: 13.5, lineHeight: 1.75, color: '#2A3444' }}>
        {displayedText}
        {isLong && (
          <Box
            component='span'
            onClick={() => {
              if (!expanded) reportUserInteraction(InteractionType.description);
              setExpanded(!expanded);
            }}
            sx={{ ml: 0.8, fontWeight: 800, color: '#0C1E3C', cursor: 'pointer' }}
          >
            {expanded ? 'Less' : 'More'}
          </Box>
        )}
      </Typography>,
      InteractionType.description,
    );
  };

  const renderPropertyFacts = () => {
    const facts = [
      { label: 'Property Type', value: data?.propertyType || data?.type },
      { label: 'Property Sub Type', value: data?.propertySubType || data?.subType },
      { label: 'Year Built', value: data?.yearBuilt },
      { label: 'Living Area', value: data?.livingArea ? `${formatNumber(data.livingArea)} sq ft` : null },
      { label: 'School District', value: data?.schoolDistrict },
      { label: 'Lot Size', value: data?.lotSizeSquareFeet ? `${formatNumber(data.lotSizeSquareFeet)} sq ft` : data?.lotSizeAcres ? `${data.lotSizeAcres} acres` : null },
      { label: 'Stories', value: data?.storiesTotal || data?.stories },
      { label: 'Bedrooms', value: data?.bedrooms || data?.bedsTotal },
      { label: 'Bathrooms Full', value: data?.bathroomsFull || data?.bathsFull },
      { label: 'Bathrooms Half', value: data?.bathroomsHalf || data?.bathsHalf },
      { label: 'Garage', value: data?.features?.garageSpaces ? `${data.features.garageSpaces} car` : null },
      { label: 'Parking Total', value: data?.features?.parkingTotal },
      { label: 'Days On Market', value: daysOnMarket },
    ].filter((item) => IsNotNullOrEmpty(item.value));

    if (facts.length === 0) return null;

    return renderSectionCard(
      'Property Facts',
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.2 }}>
        {facts.map((fact) => (
          <Box key={fact.label} sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 10, color: '#7A8596', textTransform: 'uppercase' }}>{fact.label}</Typography>
            <Typography sx={{ mt: 0.25, fontSize: 13, fontWeight: 800, color: '#0C1E3C', wordBreak: 'break-word' }}>
              {fact.value}
            </Typography>
          </Box>
        ))}
      </Box>,
      InteractionType.propertyFacts,
    );
  };

  const renderFeatureChips = () => {
    const rawFeatures = [
      ...(Array.isArray(data?.keyFeatures) ? data?.keyFeatures ?? [] : []),
      ...(Array.isArray(data?.features?.interiorFeatures) ? data?.features?.interiorFeatures ?? [] : []),
      ...(Array.isArray(data?.features?.appliances) ? data?.features?.appliances ?? [] : []),
      ...(Array.isArray(data?.features?.flooring) ? data?.features?.flooring ?? [] : []),
      ...(Array.isArray(data?.features?.exteriorFeatures) ? data?.features?.exteriorFeatures ?? [] : []),
      ...(Array.isArray(data?.features?.communityFeatures) ? data?.features?.communityFeatures ?? [] : []),
    ].filter(Boolean);
    const features = Array.from(new Set(rawFeatures.map((item: any) => String(item))));

    if (features.length === 0) return null;

    return renderSectionCard(
      'Interior Features & Appliances',
      <>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
          {features.slice(0, 20).map((feature) => (
            <Chip
              key={feature}
              label={feature}
              size='small'
              sx={{ bgcolor: 'rgba(16,185,129,0.08)', color: '#16A34A', fontSize: 11, fontWeight: 700 }}
            />
          ))}
        </Box>
        {features.length > 20 && (
          <Button onClick={keyFeaturesOnOpen} sx={{ mt: 1, p: 0, minWidth: 0, textTransform: 'none', fontSize: 12 }}>
            View all {features.length} highlights
          </Button>
        )}
      </>,
      InteractionType.features,
    );
  };

  const renderNeighborhood = () =>
    renderSectionCard(
      'Neighborhood',
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
        {[
          { label: 'Walkability', value: 75 },
          { label: 'Transit', value: 68 },
          { label: 'Bike Score', value: 82 },
          { label: 'Quiet', value: 90 },
        ].map((score) => (
          <Box key={score.label} sx={{ bgcolor: '#F8F9FA', border: '1px solid #E5E7EB', borderRadius: 2, p: 1.2, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 22, fontWeight: 800, color: '#0C1E3C' }}>
              {score.value}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#6B7280', mt: 0.3 }}>
              {score.label}
            </Typography>
          </Box>
        ))}
      </Box>,
      InteractionType.neighborhood,
    );

  const renderSchoolsSection = () => {
    const schools = [
      { label: 'Elementary', value: data?.elementarySchool },
      { label: 'Middle', value: data?.middleOrJuniorSchool },
      { label: 'High', value: data?.highSchool },
    ].filter((school) => IsNotNullOrEmpty(school.value));

    if (schools.length === 0 && !data?.schoolDistrict) return null;

    return renderSectionCard(
      'Schools',
      <>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
          {schools.map((school) => (
            <Box key={school.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#F8F9FA', borderRadius: 2, p: 1.2 }}>
              <SchoolIcon sx={{ color: '#C9A84C', fontSize: 22 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 10, color: '#6B7280', textTransform: 'uppercase' }}>{school.label}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#1A1A2E' }} noWrap>{school.value}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
        {data?.schoolDistrict && (
          <Box sx={{ mt: 1.2, bgcolor: '#FEF3C7', color: '#78350F', borderRadius: 2, p: 1.2, fontSize: 12, lineHeight: 1.5 }}>
            {data.schoolDistrict} school district
          </Box>
        )}
      </>,
      InteractionType.schools,
    );
  };

  const renderMajorSystems = () => {
    const currentYear = new Date().getFullYear();
    const yearBuilt = Number(data?.yearBuiltEffective || data?.yearBuilt || 0);
    const age = yearBuilt ? currentYear - yearBuilt : null;
    const systemStatus = (limitGood: number, limitWatch: number) => ({
      status: age && age <= limitGood ? 'New' : age && age <= limitWatch ? 'Good' : 'Watch',
      color: age && age <= limitGood ? '#16A34A' : age && age <= limitWatch ? '#D97706' : '#DC2626',
    });
    const roof = toList(data?.features?.roof).join(', ');
    const heating = toList(data?.features?.heating).join(', ');
    const cooling = toList(data?.features?.cooling).join(', ');
    const water = toList(data?.features?.waterSource).join(', ');
    const electric = toList(data?.features?.electric).join(', ');
    const systems = [
      (roof || age) && { icon: <RoofingIcon />, name: 'Roof', details: roof || 'Age based on year built', ...systemStatus(5, 15) },
      (heating || cooling || age) && { icon: <AcUnitIcon />, name: 'HVAC', details: [heating, cooling].filter(Boolean).join(' / ') || 'Age based on year built', ...systemStatus(5, 15) },
      (water || age) && { icon: <WaterDropIcon />, name: 'Water', details: water || 'Age based on year built', ...systemStatus(5, 12) },
      (electric || age) && { icon: <ElectricalServicesIcon />, name: 'Electrical', details: electric || 'Age based on year built', ...systemStatus(5, 20) },
    ].filter(Boolean) as Array<{ icon: React.ReactNode; name: string; details: string; status: string; color: string }>;

    if (systems.length === 0) return null;

    return renderSectionCard(
      'Major Systems',
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
        {systems.map((system) => (
          <Box key={system.name} sx={{ bgcolor: '#F8F9FA', border: '1px solid #E5E7EB', borderRadius: 2, p: 1.2 }}>
            <Box sx={{ color: '#C9A84C', display: 'flex', mb: 0.7 }}>{system.icon}</Box>
            <Typography sx={{ fontSize: 10, color: '#6B7280', textTransform: 'uppercase' }}>{system.name}</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: system.color }}>{system.status}</Typography>
            <Typography sx={{ fontSize: 11, color: '#6B7280', lineHeight: 1.35 }}>{system.details}</Typography>
          </Box>
        ))}
      </Box>,
      InteractionType.majorSystems,
    );
  };

  const renderPriceHistory = () => {
    const history = [
      data?.listDate && {
        date: formatMonthYear(data.listDate),
        event: 'Listed',
        price: formatCurrency(data?.listPrice),
        badge: 'Active',
      },
      data?.closePrice && data?.closeDate && {
        date: formatMonthYear(data.closeDate),
        event: 'Sold',
        price: formatCurrency(data.closePrice),
        badge: 'Closed',
      },
      data?.originalListPrice && data?.originalListPrice !== data?.listPrice && {
        date: formatMonthYear(data?.originalEntryTimestamp || data?.listDate),
        event: 'Original list price',
        price: formatCurrency(data.originalListPrice),
        badge: 'History',
      },
    ].filter(Boolean) as Array<{ date: string; event: string; price: string; badge: string }>;

    if (history.length === 0 && !data?.pricePerSquareFoot) return null;

    return renderSectionCard(
      'Price & Market',
      <>
        {history.map((item) => (
          <Box key={`${item.event}-${item.price}`} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid #F3F4F6' }}>
            <Box>
              <Typography sx={{ fontSize: 12, color: '#6B7280' }}>{item.date}</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1A1A2E' }}>{item.event}</Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#1A1A2E' }}>{item.price}</Typography>
              <Chip label={item.badge} size='small' sx={{ height: 20, fontSize: 10, bgcolor: '#EEF4FF', color: '#1E40AF', fontWeight: 700 }} />
            </Box>
          </Box>
        ))}
        {data?.pricePerSquareFoot && (
          <Box
            onClick={(event) => {
              event.stopPropagation();
              reportUserInteraction(InteractionType.marketAnalysis);
            }}
            sx={{ mt: 1.3, bgcolor: '#0C1E3C', color: '#fff', borderRadius: 2, p: 1.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#C9A84C', mb: 0.5 }}>
              <TrendingUpIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 800 }}>Market Analysis</Typography>
            </Box>
            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.68)' }}>
              {formatCurrency(data.pricePerSquareFoot)} per square foot
            </Typography>
          </Box>
        )}
      </>,
      InteractionType.priceHistory,
    );
  };

  const renderSpecialFeatures = () => {
    const specialFeatures = [
      data?.features?.garageSpaces && {
        icon: <LocalParkingIcon />,
        label: 'Garage',
        value: `${data.features.garageSpaces} car`,
        subtitle: 'Covered parking',
        color: '#1E40AF',
      },
      data?.features?.fireplaceYn && {
        icon: <FireplaceIcon />,
        label: 'Fireplace',
        value: data?.features?.fireplaceTotal ? `${data.features.fireplaceTotal} fireplace(s)` : 'Yes',
        subtitle: toList(data?.features?.fireplaceFeatures).join(', ') || 'Cozy ambiance',
        color: '#DC2626',
      },
      (data?.roomsTotal || data?.numberOfRooms) && {
        icon: <MeetingRoomIcon />,
        label: 'Rooms',
        value: data?.roomsTotal || data?.numberOfRooms,
        subtitle: 'Total rooms',
        color: '#0C1E3C',
      },
    ].filter(Boolean) as Array<{ icon: React.ReactNode; label: string; value: string | number; subtitle: string; color: string }>;

    if (specialFeatures.length === 0) return null;

    return renderSectionCard(
      'Special Features',
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
        {specialFeatures.map((feature) => (
          <Box key={feature.label} sx={{ bgcolor: '#F8F9FA', border: '1px solid #E5E7EB', borderRadius: 2, p: 1.2 }}>
            <Box sx={{ color: feature.color, display: 'flex', mb: 0.7 }}>{feature.icon}</Box>
            <Typography sx={{ fontSize: 10, color: '#6B7280', textTransform: 'uppercase' }}>{feature.label}</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: feature.color }}>{feature.value}</Typography>
            <Typography sx={{ fontSize: 11, color: '#6B7280', lineHeight: 1.35 }}>{feature.subtitle}</Typography>
          </Box>
        ))}
      </Box>,
      InteractionType.rooms,
    );
  };

  const renderFinancialDetails = () => {
    const financialRows = [
      {
        icon: <AccountBalanceIcon />,
        label: 'Property Tax',
        value: data?.taxAnnualAmount ? `${formatCurrency(data.taxAnnualAmount)}/yr` : '-',
        flag: data?.taxAnnualAmount ? `${formatCurrency(Math.round(Number(data.taxAnnualAmount) / 12))}/mo` : '',
      },
      {
        icon: <AttachMoneyIcon />,
        label: 'HOA Fee',
        value: data?.association?.associationFee
          ? `${formatCurrency(data.association.associationFee)}/${data.association.associationFrequency || 'mo'}`
          : 'None',
      },
      {
        icon: <NumbersIcon />,
        label: 'MLS Number',
        value: data?.mlsNumber || data?.listingId || '-',
      },
      {
        icon: <CalendarMonthIcon />,
        label: 'Listing Date',
        value: formatDate(data?.listDate),
      },
    ];

    return renderSectionCard(
      'Ownership Costs',
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
        {financialRows.map((row) => (
          <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#F8F9FA', borderRadius: 2, p: 1.2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Box sx={{ color: '#C9A84C', display: 'flex' }}>{row.icon}</Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 10, color: '#6B7280', textTransform: 'uppercase' }}>{row.label}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#1A1A2E' }} noWrap>{row.value}</Typography>
              </Box>
            </Box>
            {'flag' in row && row.flag && (
              <Chip label={row.flag} size='small' sx={{ height: 22, fontSize: 10, bgcolor: '#EEF4FF', color: '#1E40AF', fontWeight: 700 }} />
            )}
          </Box>
        ))}
      </Box>,
      InteractionType.financialDetails,
    );
  };

  const renderAllActions = () => {
    const visibleActions = [
      'schedule_test_drive',
      'price_and_payments',
      'request_finance',
      'virtual_walkaround',
      'history',
      'similar_properties',
      'contact_dealer',
      'share_property',
    ] as NewLeadQuickOption[];

    return (
      <Box sx={{ p: 1.2, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 0.9, bgcolor: '#F5F7FB' }}>
        {visibleActions.map((code) => {
          const item = getAction(code);
          if (!item) return null;
          return (
            <Card
              key={code}
              onClick={() => {
                if (item.code === 'similar_properties') {
                  similarCarsOnOpen();
                  reportUserInteraction(InteractionType.compareProperties);
                  return;
                }
                if (item.code === 'contact_dealer') {
                  contactDealerOnOpen();
                  reportUserInteraction(InteractionType.contactAgent);
                  return;
                }
                handleQuickOptionClicked(item);
              }}
              sx={{
                minHeight: 82,
                borderRadius: 2,
                boxShadow: '0 8px 22px rgba(12,30,60,0.08)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                gap: 0.7,
                p: 0.7,
                cursor: 'pointer',
              }}
            >
              <Box component='img' src={item.icon} alt={item.title} sx={{ width: 30, height: 30, objectFit: 'contain' }} />
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#1E293B', lineHeight: 1.15, whiteSpace: 'pre-line' }}>
                {item.shortTitle}
              </Typography>
            </Card>
          );
        })}
      </Box>
    );
  };

  const renderStickyActions = () => (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 18,
        display: 'flex',
        gap: 1,
        p: 1.2,
        bgcolor: 'rgba(255,255,255,0.96)',
        borderTop: '1px solid #E5E7EB',
        backdropFilter: 'blur(10px)',
      }}
    >
      <Button
        fullWidth
        variant='contained'
        onClick={() => handleQuickOptionClicked(getAction('schedule_test_drive'))}
        sx={{
          bgcolor: '#0C1E3C',
          color: '#fff',
          borderRadius: 2,
          py: 1.2,
          fontWeight: 800,
          textTransform: 'none',
          '&:hover': { bgcolor: '#142849' },
        }}
      >
        Book Showing
      </Button>
      <Button
        fullWidth
        variant='contained'
        onClick={() => {
          if (data?.virtualTourUrlUnbranded) {
            window.open(data.virtualTourUrlUnbranded, '_blank');
            reportUserInteraction(InteractionType.virtualTour);
          } else {
            handleQuickOptionClicked(getAction('contact_dealer'));
          }
        }}
        sx={{
          bgcolor: '#C9A84C',
          color: '#0C1E3C',
          borderRadius: 2,
          py: 1.2,
          fontWeight: 800,
          textTransform: 'none',
          '&:hover': { bgcolor: '#E8C46A' },
        }}
      >
        {data?.virtualTourUrlUnbranded ? '3D Tour' : 'Message'}
      </Button>
    </Box>
  );

  const renderPropertyDialogContent = () => (
    <Box
      sx={{
        height: isMobile ? '100vh' : '86vh',
        overflowY: 'auto',
        bgcolor: '#F5F7FB',
        pb: 0,
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'rgba(12,30,60,0.25)',
          borderRadius: 8,
        },
      }}
    >
      {renderDialogHeader()}
      {error ? (
        <Box sx={{ p: 2 }}>
          <Typography sx={{ color: 'error.main', fontSize: 13 }}>
            {error}
          </Typography>
        </Box>
      ) : (
        <>
          {renderHeroSection()}
          {renderStatsBar()}
          {renderActionCards()}
          {renderOpenHouseBanner()}
          {renderPhotoGallery()}
          {renderAgentContactCard()}
          {renderIntentScore()}
          {renderPaymentCalculator()}
          {renderDescription()}
          {renderPropertyFacts()}
          {renderNeighborhood()}
          {renderSchoolsSection()}
          {renderFeatureChips()}
          {renderMajorSystems()}
          {renderPriceHistory()}
          {renderSpecialFeatures()}
          {renderFinancialDetails()}
          {/* {renderAllActions()} */}
          {renderStickyActions()}
        </>
      )}
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={isMobile}
        maxWidth='sm'
        fullWidth
        sx={{
          '& .MuiDialog-container': {
            alignItems: isMobile ? 'stretch' : 'flex-end',
            justifyContent: isMobile ? 'center' : 'flex-end',
          },
        }}
        PaperProps={{
          sx: {
            bgcolor: '#F5F7FB',
            backgroundImage: 'none',
            width: { xs: '100%', sm: 430 },
            maxHeight: isMobile ? '100vh' : '86vh',
            m: isMobile ? 0 : 2,
            mb: isMobile ? 0 : '85px',
            overflow: 'hidden',
          },
        }}
      >
        {localLoading ? (
          <Loader />
        ) : (
          <>{renderPropertyDialogContent()}</>
        )}
      </Dialog>

      <KeyFeatureDailog
        alertTemplateToggle={keyFeaturesToggle}
        alertTemplateOnClose={keyFeaturesOnClose}
        features={data?.keyFeatures || []}
      />

      {newLeadCaptureToggle && newLeadOption && (
        <Dialog
          open={newLeadCaptureToggle}
          onClose={() => closeOnboardingForm()}
          fullWidth
          maxWidth='sm'
          PaperProps={{
            sx: {
              borderRadius: { xs: 2, sm: 3 },
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              overflow: 'visible', // Changed from 'hidden' to allow image to overflow
              position: 'relative',
              mt: 8, // Add margin top to make room for the image
              // width: "calc(100% - 10px)"
            },
          }}
        >
          {/* Rounded image anchored at top center */}
          {/* <Box
            sx={{
              position: 'absolute',
              top: -30,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1,
              width: 60,
              height: 60,
              borderRadius: '50%',
              overflow: 'hidden', // This ensures the image is clipped to circle
              border: '4px solid white',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              backgroundColor: 'white', // Fallback background
              p: 1,
            }}
          >
            <Box
              component='img'
              src={
                quickOptions.find((item) => item.code === newLeadOption.code)
                  ?.icon || 'https://miles-cdn.qrbuddy.ai/static/qr_buddy_logo_blue_small.png'
              }
              alt='Dialog header'
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover', // Ensures image covers the circle without distortion
                objectPosition: 'center', // Centers the image
              }}
            />
          </Box> */}

          <DialogContent
            sx={{
              // p: { xs: 2, sm: 3 }
              p: 0,
              // pt: 2,
            }}
          >
            {['value_trade', 'virtual_walkaround', 'history'].includes(
              newLeadOption.code,
            ) &&
              newLeadStep === 'information' && (
                <PreOnboardingDetails
                  onContinue={() => handleQuickOptionClicked(newLeadOption)}
                  onBack={() => {
                    closeOnboardingForm();
                  }}
                  newLeadOption={newLeadOption.code}
                  title={newLeadOption.infoDialogDetails?.title}
                  description={newLeadOption.infoDialogDetails?.description}
                  featureTitle={newLeadOption.infoDialogDetails?.featureTitle}
                  featureDescription={
                    newLeadOption.infoDialogDetails?.featureDescription
                  }
                  features={newLeadOption.infoDialogDetails?.features ?? []}
                  submitButtonText={
                    newLeadOption.infoDialogDetails?.submitButtonText
                  }
                  footerText={newLeadOption.infoDialogDetails?.footerText}
                  imagePath={newLeadOption.infoDialogDetails?.imagePath}
                />
              )}

            {newLeadOption.code === 'price_and_payments' &&
              newLeadStep === 'information' && (
                <PaymentInformationModel
                  carData={data}
                  onSubmit={(downPayment: number) => {
                    setDownPaymentAmount(downPayment);
                    handleQuickOptionClicked(newLeadOption, downPayment);
                  }}
                  onBack={() => {
                    closeOnboardingForm();
                  }}
                  gotoDealershipWebsite={gotoDealershipWebsite}
                />
              )}

            {[
              'schedule_test_drive',
              'value_trade',
              'virtual_walkaround',
              'request_finance',
              'price_and_payments',
              'history',
              'live_agent',
            ].includes(newLeadOption.code) &&
              newLeadStep === 'onboarding' && (
                <OnboardingForm
                  onSubmit={handleSubmitOnboarding}
                  onBack={function (): void {
                    // newLeadCaptureOnClose();
                    closeOnboardingForm();
                  }}
                  newLeadOption={newLeadOption.code}
                  isLoading={isLoading}
                  title={newLeadOption.onboardingDialogDetails?.title}
                  description={
                    newLeadOption.onboardingDialogDetails?.description
                  }
                  submitButtonText={
                    newLeadOption.onboardingDialogDetails?.submitButtonText
                  }
                  footerText={newLeadOption.onboardingDialogDetails?.footerText}
                  imagePath={newLeadOption.onboardingDialogDetails?.imagePath}
                />
              )}

            {[
              'schedule_test_drive',
              'value_trade',
              'virtual_walkaround',
              'request_finance',
              'price_and_payments',
              'history',
              'live_agent',
            ].includes(newLeadOption.code) &&
              newLeadStep === 'otp' && (
                <OTPVerification
                  onVerify={handleVerifyOTP}
                  onBack={function (): void {
                    newLeadCaptureOnClose();
                  }}
                  isLoading={isLoading}
                  handleResend={handleResend}
                  timeLeft={timeLeft}
                  isResendDisabled={isResendDisabled}
                />
              )}

            {newLeadOption.code === 'schedule_test_drive' &&
              newLeadStep === 'confirmation' && (
                <MilesTimeSlotPicker
                  onSelectTimeSlot={async function (
                    _datetimeUTC: string,
                    _label: string,
                    rawDate: Date,
                  ): Promise<void> {
                    const payload = {
                      propertyId: data?.id ?? '',
                      actionType: LeadActionType.scheduleTestDrive,
                      scheduleDate: rawDate.toISOString(),
                    };

                    closeOnboardingForm();

                    const result = await api.requestLeadAction(payload);
                    if (result) {
                      toast.success(
                        'Information submitted successfully. An agent will be in touch shortly.',
                      );
                    }
                  }}
                  config={config}
                  api={api}
                />
              )}

            {newLeadOption.code === 'request_finance' &&
              newLeadStep === 'confirmation' && (
                <>
                  {/* <Box
                    sx={{
                      p: 3,
                      overflowY: 'auto',
                      flex: 1,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant='h6'
                      fontWeight={600}
                      sx={{ mb: 2, textAlign: 'center' }}
                    >
                      {t('here_the_finance_app_that_your_requested')}
                    </Typography>
                    <Typography
                      variant='body2'
                      color='text.primary'
                      sx={{ mb: 3, textAlign: 'center' }}
                    >
                      {t('this_is_a_secure_link')}
                    </Typography>

                    <Box width={'100%'} textAlign={'center'}>
                      {financeResponse?.url ? (
                        <>
                          <a
                            href={financeResponse?.url}
                            target='_blank'
                            rel='noopener noreferrer'
                            style={{
                              textDecoration: 'none',
                              color: 'inherit',
                            }}
                          >
                            {shortenUrl(financeResponse?.url)}
                          </a>

                          <IconButton
                            onClick={() => {
                              handleCopyLink(financeResponse?.url);
                              closeOnboardingForm();
                            }}
                            sx={{ fontSize: '1.2rem' }}
                          >
                            <FaCopy />
                          </IconButton>
                        </>
                      ) : (
                        '-'
                      )}

                      {financeResponse?.url && (
                        <Button
                          variant='contained'
                          color='success'
                          fullWidth
                          sx={{ mt: 2, color: 'white' }}
                          onClick={() => {
                            window.open(financeResponse?.url, '_blank');
                            closeOnboardingForm();
                          }}
                        >
                          {t('button.go_to_finance_app')}
                        </Button>
                      )}
                    </Box>
                  </Box> */}
                  <PreOnboardingDetails
                    onContinue={() => handleQuickOptionClicked(newLeadOption)}
                    onBack={() => {
                      closeOnboardingForm();
                    }}
                    isLoading={isLeadActionLoading}
                    newLeadOption={newLeadOption.code}
                    title={newLeadOption.infoDialogDetails?.title}
                    description={newLeadOption.infoDialogDetails?.description}
                    featureTitle={newLeadOption.infoDialogDetails?.featureTitle}
                    featureDescription={
                      newLeadOption.infoDialogDetails?.featureDescription
                    }
                    features={newLeadOption.infoDialogDetails?.features ?? []}
                    submitButtonText={
                      newLeadOption.infoDialogDetails?.submitButtonText
                    }
                    footerText={newLeadOption.infoDialogDetails?.footerText}
                    imagePath={newLeadOption.infoDialogDetails?.imagePath}
                  />
                </>
              )}

            {newLeadOption.code === 'share_property' && (
              <>
                <Box
                  sx={{
                    pb: 1,
                    overflowY: 'auto',
                    borderRadius: { xs: 2, sm: 3 },
                    flex: 1,
                    background: BACKGROUND_GRADIENT_TRANSPARENT,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 3,
                      py: 1,
                      mb: 2,
                      textAlign: 'center',
                      background:
                        'linear-gradient(to right, #B1CAFF, #ffffff, #ffffff, #ffffff)',
                      borderBottom: '1px solid',
                      borderColor: (theme) =>
                        alpha(theme.palette.primary.light, 0.1),
                    }}
                  >
                    <IconButton
                      onClick={newLeadCaptureOnClose}
                      sx={{ mr: 0.5, p: 0.5, pl: 0 }}
                      aria-label='Go back'
                    >
                      <KeyboardBackspace sx={{ color: 'primary.light' }} />
                    </IconButton>
                    <Typography fontWeight={400} color={'primary.light'}>
                      Back to Property
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant='h5'
                      fontWeight={500}
                      sx={{ mb: 2, textAlign: 'center' }}
                    >
                      Share This Property
                    </Typography>

                    {photoSource?.[0] ? (
                      <CardMedia
                        component='img'
                        image={getAssetURL(
                          config.assetsUrl,
                          photoSource[0]?.key,
                          photoSource[0]?.url,
                        ) || ''}
                        alt={data?.title || address}
                        sx={{
                          width: '100%',
                          aspectRatio: '16 / 9',
                          objectFit: 'cover',
                          borderRadius: 1,
                          mb: 3,
                          boxShadow: 1,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          aspectRatio: '16 / 9',
                          borderRadius: 1,
                          mb: 3,
                          boxShadow: 1,
                          bgcolor: '#0C1E3C',
                          color: '#C9A84C',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <HomeWorkIcon sx={{ fontSize: 48 }} />
                      </Box>
                    )}

                    <Typography
                      variant='body1'
                      fontWeight={500}
                      sx={{ textAlign: 'center' }}
                    >
                      {data?.title}
                    </Typography>

                    <Divider
                      sx={{
                        my: 1,
                        width: '100%',
                        // borderColor: 'rgba(102, 126, 234, 0.2)'
                      }}
                    />

                    <Box width={'100%'} textAlign={'center'}>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ textAlign: 'center' }}
                      >
                        Copy link and paste it anywhere to share this property.
                      </Typography>

                      {/* {data?.link ? (
                      <>
                        <a
                          href={data?.link}
                          target='_blank'
                          rel='noopener noreferrer'
                          style={{
                            textDecoration: 'none',
                            color: 'inherit',
                          }}
                        >
                          {shortenUrl(data?.link)}
                        </a>

                        <IconButton
                          onClick={() => {
                            handleCopyLink(data?.link || undefined);
                            closeOnboardingForm();
                          }}
                          sx={{ fontSize: '1.2rem' }}
                        >
                          <FaCopy />
                        </IconButton>
                      </>
                    ) : (
                      '-'
                    )} */}

                      {data?.link && (
                        <Button
                          variant='contained'
                          fullWidth
                          sx={{ mt: 2, background: BUTTON_BG_COLOR }}
                          startIcon={<ContentCopy />}
                          onClick={() => {
                            handleCopyLink(data?.link || undefined);
                            closeOnboardingForm();
                          }}
                        >
                          Copy Link
                        </Button>
                      )}
                    </Box>

                    {/* <Card
                      sx={{
                        mt: 2,
                        p: 1,
                        bgcolor: '#F1ECF7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          alignItems: 'flex-start',
                          flex: 1,
                        }}
                      >
                        <BookmarkBorder color='primary' />
                        <Typography
                          sx={{ fontSize: typographyClamps.fontSize14 }}
                        >
                          {t('save_this_vehicle')}
                          <br />
                          <Typography
                            color='text.secondary'
                            sx={{ fontSize: typographyClamps.fontSize11 }}
                          >
                            {t('save_vehicle_login_prompt')}
                          </Typography>
                        </Typography>
                      </Box>

                      <Button
                        variant='outlined'
                        sx={{
                          m: 0,
                          fontSize: '10px',
                          px: 1,
                          borderRadius: '1px',
                        }}
                        onClick={gotoLoginPage}
                      >
                        {t('button.log_in_to_save')}
                      </Button>
                    </Card> */}
                  </Box>
                </Box>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}

      {similarCarsToggle && (
        <ViewSimilarPropertiesDialog
          toggle={similarCarsToggle}
          onClose={similarCarsOnClose}
          gotoLoginPage={() => { }}
          onVehicleSelected={(selectedVehicle: any) => {
            if (selectedVehicle?.id !== data?.id) {
              setPropertyData(null);
              setPropertyId(selectedVehicle.id);
              // navigate(removeHost(selectedVehicle.link), {
              //   replace: true,
              // });
            }
            similarCarsOnClose();
          }}
          onGetTodaysPriceClicked={(selectedVehicle: any) => {
            if (selectedVehicle?.id !== data?.id) {
              setPropertyData(selectedVehicle);
              // navigate(removeHost(selectedVehicle.link), {
              //   replace: true,
              // });
            }

            handleQuickOptionClicked(
              actionButtons.find((item) => item.code === 'price_and_payments'),
            );
          }}
          carData={data}
          api={api}
          config={config}
        />
      )}

      {contactDealerToggle && (
        <AgentContactDialog
          open={contactDealerToggle}
          onClose={contactDealerOnClose}
          handleOpenLocation={() => handleOpenLocation()}
          dealerSettings={dealerSettingsData}
          dealershipName={brokerageName || agentName}
        />
      )}
    </>
  );
};

export default PropertyDetailsDialog;
